import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";

const Refund = () => {
  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <main className="pt-20">
        <div className="max-w-4xl mx-auto px-4 py-12">
          <h1 className="text-4xl font-heading font-bold mb-6">Refund Policy</h1>
          <p className="text-sm text-muted-foreground mb-8">Last updated: {new Date().toLocaleDateString('en-GB', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          })}</p>
          
          <div className="prose prose-gray max-w-none space-y-8">
            <section>
              <h2 className="text-2xl font-heading font-semibold mb-4">1. Flexaura's Role as Marketplace</h2>
              <p className="text-muted-foreground leading-relaxed">
                Flexaura operates solely as a marketplace connecting customers with licensed financial partners 
                and merchants. We do not provide credit, loans, or financing directly, and we do not handle 
                merchant transactions or product/service delivery.
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 my-4">
                <p className="text-amber-800 font-medium">Important Notice:</p>
                <ul className="list-disc list-inside text-amber-700 space-y-1 mt-2">
                  <li>Flexaura does not sell products or services directly</li>
                  <li>All purchases are made directly with merchants</li>
                  <li>All financing is provided by our licensed partners</li>
                  <li>Flexaura does not process payments or hold funds</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-semibold mb-4">2. Merchant Refunds</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                All refund requests for products or services must be handled directly with the merchant 
                where you made your purchase. Each merchant has their own refund policy and procedures.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-blue-800 font-medium">For Product/Service Refunds:</p>
                <ol className="list-decimal list-inside text-blue-700 space-y-1 mt-2">
                  <li>Contact the merchant directly using their customer service channels</li>
                  <li>Reference your order number and purchase details</li>
                  <li>Follow the merchant's specific refund policy and timeline</li>
                  <li>Keep all documentation for your records</li>
                </ol>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                <strong>Flexaura cannot:</strong> Process refunds on behalf of merchants, intervene in 
                merchant-customer disputes, or guarantee merchant refund approvals.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-semibold mb-4">3. Financial Partner Refunds & Disputes</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                If you have financed your purchase through one of our partner providers (Tabby, Tamara, 
                Cashew, or bank partners), any issues with your financing arrangement must be handled 
                directly with that financial partner.
              </p>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <p className="text-red-800 font-medium">For Financing Issues:</p>
                <ul className="list-disc list-inside text-red-700 space-y-1 mt-2">
                  <li>Payment disputes must be resolved directly with your financial partner</li>
                  <li>Interest charges and fees are governed by your financing agreement</li>
                  <li>Flexaura is not a party to your financing agreement</li>
                  <li>Contact your financial partner's customer service for assistance</li>
                </ul>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                If a merchant approves your refund and you have an active financing plan, notify your 
                financial partner immediately. Each provider has different procedures for handling 
                refunded purchases.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-semibold mb-4">4. Flexaura Marketplace Service</h2>
              <p className="text-muted-foreground leading-relaxed">
                As a free marketplace service, Flexaura does not charge customers for connecting them 
                with merchants or financial partners. There are no Flexaura-specific fees to refund.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-semibold mb-4">5. Liability Limitations</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Flexaura serves solely as an intermediary platform and bears no liability for:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li>Product or service quality, delivery, or refunds</li>
                <li>Merchant business practices or policies</li>
                <li>Financial partner terms, conditions, or dispute resolutions</li>
                <li>Any disputes between customers and merchants or financial partners</li>
                <li>Processing delays from merchants or financial partners</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-semibold mb-4">6. Resolving Issues</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                If you need assistance understanding the refund process or determining who to contact:
              </p>
              <div className="bg-flexaura-gray/50 rounded-lg p-4">
                <p className="font-semibold">Flexaura Support</p>
                <p className="text-muted-foreground">Email: hector@flexaura.me</p>
                <p className="text-sm text-muted-foreground mt-2">
                  We can provide guidance on contacting the appropriate party (merchant or financial partner) 
                  for your specific issue, but we cannot process refunds or resolve disputes on your behalf.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-semibold mb-4">7. UAE Consumer Protection</h2>
              <p className="text-muted-foreground leading-relaxed">
                UAE consumers are protected under local consumer protection laws. For serious disputes 
                that cannot be resolved directly with merchants or financial partners, you may contact:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4 mt-4">
                <li><strong>Dubai Economy:</strong> Consumer Protection Department</li>
                <li><strong>Abu Dhabi DED:</strong> Consumer Protection Centre</li>
                <li><strong>Central Bank of UAE:</strong> For financial partner complaints</li>
              </ul>
            </section>

            <section className="bg-flexaura-gray/30 rounded-lg p-6 mt-8">
              <h3 className="text-lg font-semibold mb-3">Summary of Responsibilities</h3>
              <div className="grid md:grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="font-medium text-primary mb-2">Merchants Handle:</p>
                  <ul className="text-muted-foreground space-y-1">
                    <li>• Product/service refunds</li>
                    <li>• Order cancellations</li>
                    <li>• Quality issues</li>
                    <li>• Delivery problems</li>
                  </ul>
                </div>
                <div>
                  <p className="font-medium text-accent mb-2">Financial Partners Handle:</p>
                  <ul className="text-muted-foreground space-y-1">
                    <li>• Payment disputes</li>
                    <li>• Financing terms</li>
                    <li>• Interest & fees</li>
                    <li>• Payment plans</li>
                  </ul>
                </div>
                <div>
                  <p className="font-medium text-foreground mb-2">Flexaura Provides:</p>
                  <ul className="text-muted-foreground space-y-1">
                    <li>• Marketplace connection</li>
                    <li>• Information & guidance</li>
                    <li>• Contact assistance</li>
                    <li>• Platform support</li>
                  </ul>
                </div>
              </div>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Refund;
